/**
 * 旅行回忆系统 - 旅行详情页面（创新设计）
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTravelMemories } from '../contexts/TravelMemoriesContext';
import { travelApi } from '../services/travelMemoriesApi';
import './TravelMemoriesTravelDetail.css';

const TravelMemoriesTravelDetail = () => {
  const { spaceId, travelId } = useParams();
  const navigate = useNavigate();
  const { currentSpace } = useTravelMemories();
  const [travel, setTravel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareExpiresIn, setShareExpiresIn] = useState('24h');
  const [generatingShare, setGeneratingShare] = useState(false);

  useEffect(() => {
    if (spaceId && travelId) {
      loadTravel();
    }
  }, [spaceId, travelId]);

  const loadTravel = async () => {
    try {
      setLoading(true);
      const response = await travelApi.getTravelById(spaceId, travelId);
      console.log('【TravelDetail】API响应:', response);
      if (response && response.success) {
        setTravel(response.data);
      } else {
        console.error('加载旅行详情失败: 响应格式不正确', response);
      }
    } catch (error) {
      console.error('加载旅行详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/travel-memories/spaces/${spaceId}/travels/${travelId}/edit`);
  };

  const handleDelete = async () => {
    if (!window.confirm('确定要删除这个旅行记录吗？')) {
      return;
    }

    try {
      await travelApi.deleteTravel(spaceId, travelId);
      navigate(`/travel-memories`);
    } catch (error) {
      console.error('删除旅行失败:', error);
      alert('删除失败，请重试');
    }
  };

  const handleGenerateShareLink = async () => {
    try {
      setGeneratingShare(true);
      const response = await travelApi.generateShareLink(spaceId, travelId, shareExpiresIn);
      if (response && response.success) {
        setShareLink(response.data.shareUrl);
      } else {
        alert('生成分享链接失败：' + (response?.message || '未知错误'));
      }
    } catch (error) {
      console.error('生成分享链接失败:', error);
      alert('生成分享链接失败：' + (error.response?.data?.message || '请稍后重试'));
    } finally {
      setGeneratingShare(false);
    }
  };

  const handleCopyShareLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink).then(() => {
        alert('分享链接已复制到剪贴板');
      }).catch(() => {
        alert('复制失败，请手动复制');
      });
    }
  };

  // 格式化日期（参考图：2025年2月2日）
  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}年${m}月${d}日`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return `${month}月${day}日 ${weekdays[date.getDay()]}`;
  };

  // 地点展示（参考图：成都·都江堰、青城山）
  const locationLine =
    travel?.locations && travel.locations.length > 0
      ? travel.locations.length === 1
        ? travel.locations[0]
        : travel.locations[0] + '·' + travel.locations.slice(1).join('、')
      : '';

  // 计算旅行天数
  const calculateDays = () => {
    if (!travel?.startDate || !travel?.endDate) return 0;
    const start = new Date(travel.startDate);
    const end = new Date(travel.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  if (loading) {
    return (
      <div className="tm-travel-detail-loading">
        <div className="tm-loading-spinner"></div>
        <div>加载中...</div>
      </div>
    );
  }

  if (!travel) {
    return (
      <div className="tm-travel-detail-empty">
        <div>旅行记录不存在</div>
        <button onClick={() => navigate('/travel-memories')}>返回列表</button>
      </div>
    );
  }

  const canEdit = currentSpace && (currentSpace.role === 'owner' || currentSpace.role === 'editor');
  const travelDays = calculateDays();

  // 按日期分组行程，并关联确幸语录
  const journeyByDate = {};
  if (travel.journey && travel.journey.length > 0) {
    travel.journey.forEach((item, index) => {
      const date = item.day || `Day ${index + 1}`;
      if (!journeyByDate[date]) {
        journeyByDate[date] = [];
      }
      journeyByDate[date].push(item);
    });
  }

  // 路线时间轴数据：优先 journey 按天，否则用 locations 作为节点
  const routeTimelineItems =
    travel.journey && travel.journey.length > 0
      ? Object.keys(journeyByDate)
          .sort()
          .map((date, i) => ({
            day: `D${i + 1}`,
            date,
            location: journeyByDate[date][0]?.location || '',
            activities: journeyByDate[date]
          }))
      : (travel.locations || []).map((loc, i) => ({
          day: `D${i + 1}`,
          date: '',
          location: loc,
          activities: [{ location: loc }]
        }));


  // Hero 背景：优先封面图，否则用第一张旅行照片
  const heroBackgroundUrl =
    travel.coverImage ||
    (travel.photos && travel.photos[0] && travel.photos[0].url) ||
    '';

  return (
    <div className="tm-travel-detail-new">
      {/* 1. Hero 大图：选一张照片做背景 + 白字标题与元信息 */}
      <section className="tm-detail-hero">
        <div
          className="tm-detail-hero-bg"
          style={{
            backgroundImage: heroBackgroundUrl
              ? `url('${heroBackgroundUrl.replace(/'/g, "%27")}')`
              : 'none',
            background: heroBackgroundUrl
              ? undefined
              : 'linear-gradient(135deg, #4a6572 0%, #2d3d47 100%)',
            backgroundSize: heroBackgroundUrl ? 'cover' : undefined,
            backgroundPosition: heroBackgroundUrl ? 'center' : undefined
          }}
        />
        <div className="tm-detail-hero-overlay" />
        <div className="tm-detail-hero-content">
          <button
            type="button"
            className="tm-detail-back-btn"
            onClick={() => navigate('/travel-memories')}
          >
            ← 返回首页
          </button>
          <div className="tm-detail-hero-info">
            <h1 className="tm-detail-hero-title">{travel.title || '未命名旅行'}</h1>
            {travel.subtitle && (
              <p className="tm-detail-hero-subtitle">{travel.subtitle}</p>
            )}
            <div className="tm-detail-hero-meta">
              <span className="tm-detail-meta-item">
                <span className="tm-detail-meta-icon">📅</span>
                {travel.startDate && formatDateLong(travel.startDate)} - {travel.endDate && formatDateLong(travel.endDate)}
              </span>
              {locationLine && (
                <span className="tm-detail-meta-item">
                  <span className="tm-detail-meta-icon">📍</span>
                  {locationLine}
                </span>
              )}
            </div>
          </div>
          {canEdit && (
            <div className="tm-detail-hero-actions">
              <button type="button" className="tm-detail-share-btn" onClick={() => setShowShareDialog(true)}>
                分享
              </button>
              <button type="button" className="tm-detail-edit-btn" onClick={handleEdit}>
                编辑
              </button>
              <button type="button" className="tm-detail-delete-btn" onClick={handleDelete}>
                删除
              </button>
            </div>
          )}
        </div>
      </section>

      {/* 2. 关于这次旅行：浅色圆角卡片（参考图） */}
      {travel.description && (
        <section className="tm-detail-description-section">
          <div className="tm-detail-container">
            <div className="tm-detail-description-card">
              <p className="tm-detail-description-text">{travel.description}</p>
            </div>
          </div>
        </section>
      )}


      {/* 3. 旅行视频：标题+图标，视频位在圆角卡片内 */}
      <section className="tm-detail-video-section">
        <div className="tm-detail-container">
          <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
            <span className="tm-detail-section-icon tm-detail-section-icon-video" />
            旅行视频
          </h2>
          <div className="tm-detail-video-card">
            {travel.videoUrl ? (
              <video
                className="tm-detail-video"
                src={travel.videoUrl}
                controls
                poster={travel.coverImage || undefined}
                playsInline
              />
            ) : (
              <div className="tm-detail-video-placeholder">
                <span className="tm-detail-video-placeholder-icon">▶</span>
                <span className="tm-detail-video-placeholder-text">视频位</span>
                {canEdit && (
                  <button
                    type="button"
                    className="tm-detail-video-placeholder-btn"
                    onClick={handleEdit}
                  >
                    编辑添加视频
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 4. 路线时间轴：展示当天确幸/精彩摘要 */}
      <section className="tm-detail-route-section">
        <div className="tm-detail-container">
          <h2 className="tm-detail-section-title">路线时间轴</h2>
          {routeTimelineItems.length > 0 ? (
            <div className="tm-detail-route-timeline">
              {routeTimelineItems.map((item, index) => {
                const mainActivity = item.activities && item.activities[0];
                return (
                  <div
                    key={index}
                    className="tm-detail-route-item"
                  >
                    <div className="tm-detail-route-dot" />
                    <div className="tm-detail-route-content">
                      <div className="tm-detail-route-header">
                        <div className="tm-detail-route-header-left">
                          <div className="tm-detail-route-day">{item.day}</div>
                          <h3 className="tm-detail-route-location">{item.location}</h3>
                          {item.date && (
                            <p className="tm-detail-route-date">{item.date}</p>
                          )}
                        </div>
                      </div>
                      {/* 当天多段对话/确幸 */}
                      {mainActivity &&
                        Array.isArray(mainActivity.moments) &&
                        mainActivity.moments.length > 0 && (
                          <div className="tm-detail-route-day-moments">
                            {mainActivity.moments.map((m, i) => (
                              <div
                                key={m.id || i}
                                className="tm-detail-route-day-moment"
                                style={{ ['--day-moment-accent']: m.color || '#c4a77d' }}
                              >
                                <span className="tm-detail-route-day-moment-bar" />
                                <span className="tm-detail-route-day-moment-quote">
                                  "{m.quote}"
                                </span>
                                {m.author && (
                                  <span className="tm-detail-route-day-moment-author">
                                    —{m.author}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="tm-detail-empty-state">
              <p>暂无路线信息</p>
              {canEdit && (
                <button type="button" className="tm-detail-add-btn" onClick={handleEdit}>
                  编辑行程
                </button>
              )}
            </div>
          )}
        </div>
      </section>


      {/* 5. 精彩瞬间：图标+标题，照片卡片左下角叠字（参考图） */}
      <section className="tm-detail-photos-section">
        <div className="tm-detail-container">
          <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
            <span className="tm-detail-section-icon tm-detail-section-icon-photo" />
            精彩瞬间
          </h2>
          {travel.photos && travel.photos.length > 0 ? (
            <div className="tm-detail-photos-masonry">
              {travel.photos.map((photo, index) => (
                <div
                  key={photo.id || index}
                  className="tm-detail-photo-card tm-detail-photo-card-clickable"
                  onClick={() => setLightboxPhoto(photo)}
                  onKeyDown={(e) => e.key === 'Enter' && setLightboxPhoto(photo)}
                  role="button"
                  tabIndex={0}
                  aria-label="查看大图"
                >
                  <img
                    src={photo.url}
                    alt={photo.title || '照片'}
                    loading="lazy"
                  />
                  {(photo.title || photo.location) && (
                    <div className="tm-detail-photo-caption">
                      {photo.title && (
                        <span className="tm-detail-photo-caption-title">{photo.title}</span>
                      )}
                      {photo.location && (
                        <span className="tm-detail-photo-caption-location">{photo.location}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="tm-detail-empty-state">
              <div className="tm-detail-empty-icon">📸</div>
              <p>还没有照片</p>
              {canEdit && (
                <button type="button" className="tm-detail-add-btn" onClick={handleEdit}>
                  添加照片
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* 6. 确幸语录：星标+标题，彩色卡片 + ——作者（参考图） */}
      {(travel.moments == null ? [] : travel.moments).length > 0 && (
        <section className="tm-detail-moments-section">
          <div className="tm-detail-container">
            <h2 className="tm-detail-section-title tm-detail-section-title-with-icon">
              <span className="tm-detail-section-icon tm-detail-section-icon-moment" />
              确幸语录
            </h2>
            <div className="tm-detail-moments-grid">
              {travel.moments.map((moment, index) => (
                <div
                  key={moment.id || index}
                  className="tm-detail-moment-card"
                  style={{
                    '--moment-accent': moment.color || '#c4a77d'
                  }}
                >
                  <span className="tm-detail-moment-accent" />
                  <div className="tm-detail-moment-quote">"{moment.quote}"</div>
                  <div className="tm-detail-moment-author">— {moment.author}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* 照片大图 lightbox：点击单张时展示，同一 URL 优先用浏览器缓存 */}
      {lightboxPhoto && (
        <div
          className="tm-detail-lightbox"
          onClick={() => setLightboxPhoto(null)}
          onKeyDown={(e) => e.key === 'Escape' && setLightboxPhoto(null)}
          role="dialog"
          aria-modal="true"
          aria-label="查看大图"
          tabIndex={-1}
        >
          <button
            type="button"
            className="tm-detail-lightbox-close"
            onClick={() => setLightboxPhoto(null)}
            aria-label="关闭"
          >
            ×
          </button>
          <div
            className="tm-detail-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxPhoto.url}
              alt={lightboxPhoto.title || '照片'}
            />
            {(lightboxPhoto.title || lightboxPhoto.location) && (
              <div className="tm-detail-lightbox-caption">
                {lightboxPhoto.title && <span>{lightboxPhoto.title}</span>}
                {lightboxPhoto.location && (
                  <span className="tm-detail-lightbox-location">
                    {lightboxPhoto.location}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 分享对话框 */}
      {showShareDialog && (
        <div className="tm-share-dialog-overlay" onClick={() => setShowShareDialog(false)}>
          <div className="tm-share-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="tm-share-dialog-header">
              <h3>生成分享链接</h3>
              <button className="tm-share-dialog-close" onClick={() => setShowShareDialog(false)}>×</button>
            </div>
            <div className="tm-share-dialog-content">
              <div className="tm-share-dialog-section">
                <label>有效期：</label>
                <div className="tm-share-expires-options">
                  <label className="tm-share-expires-option">
                    <input
                      type="radio"
                      name="expiresIn"
                      value="2h"
                      checked={shareExpiresIn === '2h'}
                      onChange={(e) => setShareExpiresIn(e.target.value)}
                    />
                    <span>2小时</span>
                  </label>
                  <label className="tm-share-expires-option">
                    <input
                      type="radio"
                      name="expiresIn"
                      value="24h"
                      checked={shareExpiresIn === '24h'}
                      onChange={(e) => setShareExpiresIn(e.target.value)}
                    />
                    <span>24小时</span>
                  </label>
                  <label className="tm-share-expires-option">
                    <input
                      type="radio"
                      name="expiresIn"
                      value="7d"
                      checked={shareExpiresIn === '7d'}
                      onChange={(e) => setShareExpiresIn(e.target.value)}
                    />
                    <span>7天</span>
                  </label>
                </div>
              </div>
              
              {shareLink && (
                <div className="tm-share-dialog-section">
                  <label>分享链接：</label>
                  <div className="tm-share-link-container">
                    <input
                      type="text"
                      readOnly
                      value={shareLink}
                      className="tm-share-link-input"
                    />
                    <button className="tm-share-copy-btn" onClick={handleCopyShareLink}>
                      复制
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="tm-share-dialog-footer">
              <button
                className="tm-share-generate-btn"
                onClick={handleGenerateShareLink}
                disabled={generatingShare}
              >
                {generatingShare ? '生成中...' : '生成链接'}
              </button>
              <button className="tm-share-cancel-btn" onClick={() => {
                setShowShareDialog(false);
                setShareLink(null);
              }}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelMemoriesTravelDetail;
